import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiCall } from "../lib/api";
import { 
  Send, Search, MoreVertical, Phone, Video, Paperclip, Image, 
  Smile, X, Plus, Check, CheckCheck, Clock, User, Users,
  Mic, MicOff, Square, Play, Pause, Volume2, Wifi, WifiOff
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export const MessagesPage = () => {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [typingUsers, setTypingUsers] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [wsConnected, setWsConnected] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const recognitionRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!user || wsRef.current?.readyState === WebSocket.OPEN) return;
    
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/${user.id}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected');
      setWsConnected(true);
      // Send ping every 30 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
      ws.pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'message':
          // New message received
          setMessages(prev => [...prev, data.data]);
          // Refresh conversations to update last message
          fetchConversations();
          break;
          
        case 'message_sent':
          // Message delivery confirmation
          console.log('Message delivered:', data.data.message_id);
          break;
          
        case 'typing':
          // Typing indicator
          setTypingUsers(prev => ({
            ...prev,
            [data.data.conversation_id]: data.data.is_typing ? data.data.user_name : null
          }));
          break;
          
        case 'read_receipt':
          // Update message read status
          setMessages(prev => prev.map(msg => 
            data.data.message_ids.includes(msg.id) ? { ...msg, read: true } : msg
          ));
          break;
          
        case 'online_status':
          // User online/offline
          setOnlineUsers(prev => {
            const newSet = new Set(prev);
            if (data.data.is_online) {
              newSet.add(data.data.user_id);
            } else {
              newSet.delete(data.data.user_id);
            }
            return newSet;
          });
          break;
          
        case 'pong':
          // Keep-alive response
          break;
          
        default:
          console.log('Unknown WS message type:', data.type);
      }
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setWsConnected(false);
      clearInterval(ws.pingInterval);
      // Attempt reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, 3000);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
  }, [user]);

  // Connect WebSocket on mount
  useEffect(() => {
    if (user && token) {
      connectWebSocket();
      fetchConversations();
      fetchUsers();
    }
    
    return () => {
      if (wsRef.current) {
        clearInterval(wsRef.current.pingInterval);
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [user, token, connectWebSocket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send typing indicator via WebSocket
  const sendTypingIndicator = useCallback((isTyping) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !activeConversation) return;
    
    const participantIds = activeConversation.participants || [activeConversation.other_user_id];
    
    wsRef.current.send(JSON.stringify({
      type: 'typing',
      conversation_id: activeConversation.id,
      is_typing: isTyping,
      participant_ids: participantIds,
      user_name: `${user.first_name} ${user.last_name}`
    }));
  }, [activeConversation, user]);

  // Handle input change with typing indicator
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (!isTyping) {
      setIsTyping(true);
      sendTypingIndicator(true);
    }
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      sendTypingIndicator(false);
    }, 2000);
  };

  // Initialize Web Speech API
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      
      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        if (finalTranscript) {
          setNewMessage(prev => prev + finalTranscript);
          setTranscript('');
        } else {
          setTranscript(interimTranscript);
        }
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error("Microphone access denied. Please allow microphone access in your browser settings.");
        }
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const fetchConversations = async () => {
    try {
      const data = await apiCall("GET", `/messages/conversations/${user.id}`, null, token);
      setConversations(data || []);
      if (data && data.length > 0) {
        setActiveConversation(data[0]);
        fetchMessages(data[0].id);
      }
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
      setConversations(demoConversations);
      if (demoConversations.length > 0) {
        setActiveConversation(demoConversations[0]);
        setMessages(demoMessages);
      }
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const data = await apiCall("GET", "/users", null, token);
      setUsers(data.filter(u => u.id !== user.id) || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const data = await apiCall("GET", `/messages/${conversationId}`, null, token);
      setMessages(data || []);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      setMessages(demoMessages);
    }
  };

  const sendTypingStatus = async (typing) => {
    if (!activeConversation) return;
    try {
      await apiCall("POST", "/typing/status", {
        conversation_id: activeConversation.id,
        user_id: user.id,
        is_typing: typing
      }, token);
    } catch (err) {
      // Ignore errors silently
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    // Stop typing indicator
    setIsTyping(false);
    sendTypingStatus(false);

    const messageData = {
      conversation_id: activeConversation.id,
      sender_id: user.id,
      content: newMessage.trim(),
      timestamp: new Date().toISOString()
    };

    const tempMessage = {
      ...messageData,
      id: Date.now(),
      sender_name: `${user.first_name} ${user.last_name}`,
      status: "sending"
    };
    setMessages([...messages, tempMessage]);
    setNewMessage("");
    setTranscript("");

    try {
      await apiCall("POST", "/messages", messageData, token);
      setMessages(prev => prev.map(m => 
        m.id === tempMessage.id ? { ...m, status: "sent" } : m
      ));
    } catch (err) {
      console.error("Failed to send message:", err);
      setMessages(prev => prev.map(m => 
        m.id === tempMessage.id ? { ...m, status: "delivered" } : m
      ));
    }
  };

  const handleNewConversation = async () => {
    if (!selectedRecipient) {
      toast.error("Please select a recipient");
      return;
    }

    // Check if conversation already exists
    const existingConv = conversations.find(c => 
      c.participants?.includes(selectedRecipient.id) ||
      c.other_user_id === selectedRecipient.id
    );
    
    if (existingConv) {
      setActiveConversation(existingConv);
      fetchMessages(existingConv.id);
      setShowNewMessage(false);
      setSelectedRecipient(null);
      toast.info(`Conversation with ${selectedRecipient.first_name} already exists`);
      return;
    }

    // Create a temporary conversation object for immediate UI feedback
    const tempConv = {
      id: `temp-${Date.now()}`,
      participants: [user.id, selectedRecipient.id],
      participant_names: [`${user.first_name} ${user.last_name}`, `${selectedRecipient.first_name} ${selectedRecipient.last_name}`],
      other_user_id: selectedRecipient.id,
      other_user_name: `${selectedRecipient.first_name} ${selectedRecipient.last_name}`,
      last_message: "Start your conversation...",
      last_message_time: new Date().toISOString(),
      unread_count: 0,
      isNew: true
    };

    // Add to conversations list and set as active
    setConversations(prev => [tempConv, ...prev]);
    setActiveConversation(tempConv);
    setMessages([]);
    setShowNewMessage(false);
    setSelectedRecipient(null);
    toast.success(`Started conversation with ${selectedRecipient.first_name}`);
  };

  // Modified send message to handle new conversations
  const handleSendMessageWithNewConv = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim()) return;
    
    // If this is a new temporary conversation, send with recipient_id
    if (activeConversation?.isNew) {
      const recipientId = activeConversation.other_user_id;
      
      try {
        await apiCall("POST", "/messages", {
          recipient_id: recipientId,
          subject: "Message",
          content: newMessage.trim()
        }, token);
        
        setNewMessage("");
        setTranscript("");
        
        // Refresh conversations to get the real conversation
        await fetchConversations();
        toast.success("Message sent!");
      } catch (err) {
        console.error("Failed to send message:", err);
        toast.error("Failed to send message");
      }
      return;
    }
    
    // Regular message sending for existing conversations
    handleSendMessage(e);
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        setAudioUrl(URL.createObjectURL(audioBlob));
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info("Recording started...");
    } catch (err) {
      console.error("Failed to start recording:", err);
      toast.error("Failed to access microphone. Please allow microphone access.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success("Recording stopped");
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const discardRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setIsPlaying(false);
  };

  const sendVoiceMessage = () => {
    if (audioBlob) {
      // For now, we'll just notify that voice messages would be sent
      // In production, you'd upload the blob and send a message with the audio URL
      toast.info("Voice message recorded! (Audio upload coming soon)");
      discardRecording();
    }
  };

  // Voice-to-Text Functions
  const toggleListening = () => {
    if (!recognitionRef.current) {
      toast.error("Speech recognition is not supported in your browser");
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
      toast.info("Listening... Speak now");
    }
  };

  const getOtherParticipant = (conv) => {
    if (!conv.participant_names) return "Unknown";
    const names = conv.participant_names.filter(name => 
      name !== `${user.first_name} ${user.last_name}`
    );
    return names[0] || conv.participant_names[0];
  };

  const filteredConversations = conversations.filter(conv => {
    const otherName = getOtherParticipant(conv);
    return otherName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getMessageStatus = (status) => {
    switch (status) {
      case "sending": return <Clock size={14} className="text-gray-400" />;
      case "sent": return <Check size={14} className="text-gray-400" />;
      case "delivered": return <CheckCheck size={14} className="text-blue-500" />;
      case "read": return <CheckCheck size={14} className="text-green-500" />;
      default: return <Check size={14} className="text-gray-400" />;
    }
  };

  // Demo data
  const demoConversations = [
    { id: "1", participant_names: ["You", "[Staff Name]"], last_message: "Sure, I'll check the grades", last_message_time: new Date().toISOString(), unread_count: 2 },
    { id: "2", participant_names: ["You", "[Staff Name]"], last_message: "The meeting is confirmed", last_message_time: new Date(Date.now() - 3600000).toISOString(), unread_count: 0 },
  ];

  const demoMessages = [
    { id: 1, sender_name: "[Staff Name]", content: "Hello! How can I help you today?", timestamp: new Date(Date.now() - 7200000).toISOString(), status: "read" },
    { id: 2, sender_name: `${user?.first_name || 'You'}`, content: "I wanted to ask about the upcoming exam schedule.", timestamp: new Date(Date.now() - 3600000).toISOString(), status: "delivered" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-yellow-400 to-orange-400">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="py-8 bg-gradient-to-b from-yellow-400 to-orange-400 min-h-screen" data-testid="messages-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden" style={{ height: "calc(100vh - 200px)" }}>
          <div className="grid grid-cols-1 md:grid-cols-3 h-full">
            {/* Conversations List */}
            <div className="border-r border-gray-200 flex flex-col">
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#002147] to-[#003366]">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-white">Messages</h2>
                  <Button 
                    size="sm" 
                    className="bg-[#FF6B00] hover:bg-[#E65C00]"
                    onClick={() => setShowNewMessage(true)}
                    data-testid="new-message-btn"
                  >
                    <Plus size={16} className="mr-1" /> New
                  </Button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Search conversations..."
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-300"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              
              <ScrollArea className="flex-1">
                {filteredConversations.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <User size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>No conversations yet</p>
                    <Button variant="link" className="text-[#FF6B00]" onClick={() => setShowNewMessage(true)}>
                      Start a new conversation
                    </Button>
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <div
                      key={conv.id}
                      className={`p-4 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-50 ${
                        activeConversation?.id === conv.id ? "bg-orange-50 border-l-4 border-l-[#FF6B00]" : ""
                      }`}
                      onClick={() => {
                        setActiveConversation(conv);
                        fetchMessages(conv.id);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback className="bg-[#002147] text-white">
                            {getOtherParticipant(conv).split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-[#002147] truncate">{getOtherParticipant(conv)}</p>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-[#FF6B00]">{conv.unread_count}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate">{conv.last_message}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </ScrollArea>
            </div>

            {/* Messages Area */}
            <div className="col-span-2 flex flex-col">
              {activeConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Avatar>
                            <AvatarFallback className="bg-[#002147] text-white">
                              {getOtherParticipant(activeConversation).split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          {/* Online indicator */}
                          {onlineUsers.has(activeConversation.other_user_id) && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-[#002147]">{getOtherParticipant(activeConversation)}</p>
                          {typingUsers[activeConversation.id] ? (
                            <p className="text-sm text-green-500 animate-pulse">
                              typing...
                            </p>
                          ) : onlineUsers.has(activeConversation.other_user_id) ? (
                            <p className="text-sm text-green-500">Online</p>
                          ) : (
                            <p className="text-sm text-gray-400">Offline</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* WebSocket connection status */}
                        <Badge variant="outline" className={wsConnected ? "border-green-500 text-green-500" : "border-red-500 text-red-500"}>
                          {wsConnected ? <Wifi size={12} className="mr-1" /> : <WifiOff size={12} className="mr-1" />}
                          {wsConnected ? "Live" : "Offline"}
                        </Badge>
                        <Button variant="ghost" size="sm"><Phone size={18} /></Button>
                        <Button variant="ghost" size="sm"><Video size={18} /></Button>
                        <Button variant="ghost" size="sm"><MoreVertical size={18} /></Button>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((message, index) => {
                        const isOwn = message.sender_name === `${user?.first_name} ${user?.last_name}` || 
                                      message.sender_name === `${user?.first_name}` ||
                                      message.sender_id === user?.id;
                        return (
                          <div key={message.id || index} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                              isOwn ? "bg-[#FF6B00] text-white rounded-br-none" : "bg-gray-100 text-gray-800 rounded-bl-none"
                            }`}>
                              <p>{message.content}</p>
                              <div className={`flex items-center gap-1 mt-1 text-xs ${isOwn ? "text-white/70" : "text-gray-400"}`}>
                                <span>{new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                {isOwn && getMessageStatus(message.status)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Typing Indicator */}
                      {activeConversation && typingUsers[activeConversation.id] && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 rounded-2xl px-4 py-3 rounded-bl-none">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                              </div>
                              <span className="text-xs text-gray-500">{typingUsers[activeConversation.id]} is typing...</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Voice Recording Preview */}
                  {audioUrl && (
                    <div className="px-4 py-2 bg-orange-50 border-t border-orange-200 flex items-center gap-3">
                      <audio ref={audioRef} src={audioUrl} onEnded={() => setIsPlaying(false)} />
                      <Button size="sm" variant="outline" onClick={playAudio}>
                        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                      </Button>
                      <div className="flex-1">
                        <div className="h-2 bg-orange-200 rounded-full">
                          <div className="h-2 bg-[#FF6B00] rounded-full w-0" style={{ width: isPlaying ? '100%' : '0%', transition: 'width 3s linear' }}></div>
                        </div>
                      </div>
                      <Button size="sm" className="bg-[#FF6B00] hover:bg-[#E65C00]" onClick={sendVoiceMessage}>
                        <Send size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={discardRecording}>
                        <X size={16} />
                      </Button>
                    </div>
                  )}

                  {/* Message Input */}
                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                    {/* Voice-to-Text Transcript Preview */}
                    {transcript && (
                      <div className="mb-2 px-3 py-2 bg-blue-50 rounded-lg text-sm text-blue-700 italic">
                        {transcript}...
                      </div>
                    )}
                    
                    <form onSubmit={handleSendMessageWithNewConv} className="flex items-center gap-2">
                      <Button type="button" variant="ghost" size="sm"><Paperclip size={20} /></Button>
                      <Button type="button" variant="ghost" size="sm"><Image size={20} /></Button>
                      
                      {/* Voice Recording Button */}
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        className={isRecording ? "text-red-500 animate-pulse" : ""}
                        onClick={isRecording ? stopRecording : startRecording}
                        data-testid="voice-record-btn"
                      >
                        {isRecording ? <Square size={20} /> : <Mic size={20} />}
                      </Button>
                      
                      <Input
                        placeholder={isListening ? "Listening..." : "Type a message..."}
                        value={newMessage}
                        onChange={handleInputChange}
                        className={`flex-1 ${isListening ? "border-blue-500 ring-1 ring-blue-500" : ""}`}
                        data-testid="message-input"
                      />
                      
                      {/* Voice-to-Text Button */}
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        className={isListening ? "text-blue-500 animate-pulse" : ""}
                        onClick={toggleListening}
                        data-testid="voice-to-text-btn"
                      >
                        {isListening ? <MicOff size={20} /> : <Volume2 size={20} />}
                      </Button>
                      
                      <Button type="button" variant="ghost" size="sm"><Smile size={20} /></Button>
                      <Button 
                        type="submit" 
                        className="bg-[#FF6B00] hover:bg-[#E65C00]"
                        disabled={!newMessage.trim()}
                        data-testid="send-message-btn"
                      >
                        <Send size={18} />
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Users size={64} className="mx-auto mb-4 text-gray-300" />
                    <p className="text-xl font-semibold text-[#002147]">Select a conversation</p>
                    <p className="text-gray-500">Choose from your existing conversations or start a new one</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New Message Dialog */}
      <Dialog open={showNewMessage} onOpenChange={setShowNewMessage}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Select Recipient</label>
              <Select onValueChange={(value) => {
                const selected = users.find(u => u.id === value);
                setSelectedRecipient(selected);
              }}>
                <SelectTrigger><SelectValue placeholder="Choose a person to message" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.user_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewMessage(false)}>Cancel</Button>
            <Button className="bg-[#FF6B00] hover:bg-[#E65C00]" onClick={handleNewConversation}>
              Start Conversation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesPage;
